// MUI's own CSSProperties: React's shape plus the index signature its typography
// variants require. React's lacks the index signature; `CSSProperties` is too wide.
import type { CSSProperties } from "@mui/material/styles";

import { FONT_BODY, FONT_HEADLINE, FONT_SUBHEAD, UP_DESKTOP } from "./tokens";

/**
 * The type system.
 *
 * Alongside MUI's stock variants we register the house named scale — `He1` (hero),
 * `H1–H3` (headings), `T1–T3` (body), `L1–L3` (labels/meta), `HL1–HL2` (links) and
 * `OV1` (overline). Use them anywhere via `<Typography variant="T2">`; the family is
 * baked into the variant, so a heading always gets Syncopate and body always Outfit
 * without any call site having to know that.
 *
 * The brand guide assigns each family a fixed job:
 *   Syncopate      → headlines, uppercase, wide tracking
 *   Gilda Display  → subheadlines, the serif counterpoint
 *   Outfit         → body and UI
 */

// ── Named variant styles ──────────────────────────────────────────
// The scale steps up past DESKTOP_BREAKPOINT. Putting the media query inside the
// variant means every call site gets the responsive behaviour for free — there is no
// second, ad-hoc `fontSize` override anywhere in the app.
// Syncopate has no lowercase personality to speak of and the brand always sets it
// uppercase, so the transform belongs in the variant rather than at each call site.
const He1: CSSProperties = {
  fontFamily: FONT_HEADLINE,
  fontWeight: 700,
  fontSize: "2.5rem",
  lineHeight: 1.1,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  [UP_DESKTOP]: { fontSize: "3.5rem" },
};
const H1: CSSProperties = {
  fontFamily: FONT_HEADLINE,
  fontWeight: 700,
  fontSize: "1.5rem",
  lineHeight: 1.15,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  [UP_DESKTOP]: { fontSize: "2rem" },
};
const H2: CSSProperties = {
  fontFamily: FONT_HEADLINE,
  fontWeight: 700,
  fontSize: "1.25rem",
  lineHeight: 1.2,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  [UP_DESKTOP]: { fontSize: "1.5rem" },
};
/** The serif subheadline. Sentence case — it is the calm voice, not the loud one. */
const H3: CSSProperties = {
  fontFamily: FONT_SUBHEAD,
  fontWeight: 400,
  fontSize: "1.25rem",
  lineHeight: 1.3,
  letterSpacing: "0.01em",
  [UP_DESKTOP]: { fontSize: "1.5rem" },
};

const T1: CSSProperties = {
  fontFamily: FONT_BODY,
  fontWeight: 400,
  fontSize: "1.0625rem",
  lineHeight: 1.6,
  [UP_DESKTOP]: { fontSize: "1.125rem" },
};
const T2: CSSProperties = {
  fontFamily: FONT_BODY,
  fontWeight: 400,
  fontSize: "0.9375rem",
  lineHeight: 1.55,
};
const T3: CSSProperties = {
  fontFamily: FONT_BODY,
  fontWeight: 400,
  fontSize: "0.8125rem",
  lineHeight: 1.5,
};

const L1: CSSProperties = {
  fontFamily: FONT_BODY,
  fontWeight: 500,
  fontSize: "0.9375rem",
  lineHeight: 1.35,
};
const L2: CSSProperties = {
  fontFamily: FONT_BODY,
  fontWeight: 500,
  fontSize: "0.8125rem",
  lineHeight: 1.35,
};
const L3: CSSProperties = {
  fontFamily: FONT_BODY,
  fontWeight: 500,
  fontSize: "0.6875rem",
  lineHeight: 1.4,
  letterSpacing: "0.02em",
};

/** The small uppercase eyebrow above a heading, e.g. `THE MORE YOU KNOW`. */
const OV1: CSSProperties = {
  fontFamily: FONT_HEADLINE,
  fontWeight: 700,
  fontSize: "0.6875rem",
  lineHeight: 1.3,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
};

const HL1: CSSProperties = {
  fontFamily: FONT_BODY,
  fontWeight: 500,
  fontSize: "0.9375rem",
  lineHeight: 1.5,
  color: "var(--mui-palette-link-main)",
  textDecorationLine: "underline",
};
const HL2: CSSProperties = { ...HL1, fontSize: "0.8125rem" };

export const textVariants = {
  He1,
  H1,
  H2,
  H3,
  T1,
  T2,
  T3,
  L1,
  L2,
  L3,
  OV1,
  HL1,
  HL2,
} as const;
export type TextVariant = keyof typeof textVariants;

/** Which HTML element each named variant renders as (accessibility + semantics). */
export const textVariantMapping: Record<TextVariant, string> = {
  He1: "h1",
  H1: "h1",
  H2: "h2",
  H3: "h3",
  T1: "p",
  T2: "p",
  T3: "p",
  L1: "span",
  L2: "span",
  L3: "span",
  OV1: "span",
  HL1: "a",
  HL2: "a",
};

// ── MUI type augmentation ─────────────────────────────────────────
declare module "@mui/material/styles" {
  interface TypographyVariants {
    He1: CSSProperties;
    H1: CSSProperties;
    H2: CSSProperties;
    H3: CSSProperties;
    T1: CSSProperties;
    T2: CSSProperties;
    T3: CSSProperties;
    L1: CSSProperties;
    L2: CSSProperties;
    L3: CSSProperties;
    OV1: CSSProperties;
    HL1: CSSProperties;
    HL2: CSSProperties;
  }
  interface TypographyVariantsOptions {
    He1?: CSSProperties;
    H1?: CSSProperties;
    H2?: CSSProperties;
    H3?: CSSProperties;
    T1?: CSSProperties;
    T2?: CSSProperties;
    T3?: CSSProperties;
    L1?: CSSProperties;
    L2?: CSSProperties;
    L3?: CSSProperties;
    OV1?: CSSProperties;
    HL1?: CSSProperties;
    HL2?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    He1: true;
    H1: true;
    H2: true;
    H3: true;
    T1: true;
    T2: true;
    T3: true;
    L1: true;
    L2: true;
    L3: true;
    OV1: true;
    HL1: true;
    HL2: true;
  }
}

export const typography = {
  fontFamily: FONT_BODY,
  h1: He1,
  h2: H1,
  h3: H2,
  h4: H3,
  h5: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: "1.0625rem" },
  h6: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: "0.9375rem" },
  subtitle1: { fontFamily: FONT_SUBHEAD, fontWeight: 400 },
  subtitle2: { fontFamily: FONT_BODY, fontWeight: 600 },
  body1: T1,
  body2: T2,
  caption: L3,
  button: {
    fontFamily: FONT_BODY,
    fontWeight: 600,
    textTransform: "none",
    letterSpacing: 0,
  },
  overline: OV1,
  ...textVariants,
} as const;
